import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';

import { useSwitchCompanyMutation } from '@features/auth/hooks';
import { postLogout } from '@entities/auth/api/auth';
import useAuthStore from '@entities/auth/model/useAuthStore';
import { isMobileApp } from '@shared/utils/fcmToken';
import closeIcon from '@/shared/assets/images/close_icon.svg';
import InboxIcon from '@shared/assets/images/inbox-01_icon.svg';
import BoxIcon from '@shared/assets/images/box_icon.svg';
import UmbrellaIcon from '@shared/assets/images/umbrella-03_icon.svg';
import SettingsIcon from '@shared/assets/images/settings-01_icon.svg';
import ChevronDownIcon from '@shared/assets/images/chevron-down_icon.svg';
import logoutIcon from '@shared/assets/images/log-out-01.svg';
import ApprovalNotificationDot from '@shared/components/atoms/ApprovalNotificationDot/indes';

import styles from './MobileMenu.module.scss';

/**
 * 모바일 전체 화면 메뉴 컴포넌트
 * - 햄버거 메뉴 클릭 시 전체 화면으로 표시
 * - PC 사이드바와 동일한 메뉴 구조
 */
function MobileMenu({ isOpen, onClose }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { user, clearAuth, companyList, companyCode } = useAuthStore();
  const [openMenus, setOpenMenus] = useState({
    approvalBox: false,
    archiveBox: false,
    vacationManagement: false,
    settings: false,
  });
  const [isAccountSwitchOpen, setIsAccountSwitchOpen] = useState(false);

  // 계정전환 Mutation
  const switchCompanyMutation = useSwitchCompanyMutation({
    onSuccess: () => {
      onClose();
    },
    onError: (error) => {
      console.error('계정 전환 실패:', error.friendlyMsg);
    },
  });

  // 계정전환 섹션 표시 여부 (2개 이상의 회사가 있을 때만)
  const showAccountSwitch = companyList && companyList.length > 1;

  // 계정전환 핸들러
  function handleSwitchCompany(targetCompanyCode) {
    if (switchCompanyMutation.isPending) return;
    switchCompanyMutation.mutate(targetCompanyCode);
  }

  // 계정전환 토글 (열릴 때 모든 메뉴 접기)
  function toggleAccountSwitch() {
    setIsAccountSwitchOpen((prev) => {
      // 계정전환이 열릴 때 모든 메뉴 접기
      if (!prev) {
        setOpenMenus({
          approvalBox: false,
          archiveBox: false,
          vacationManagement: false,
          settings: false,
        });
      }
      return !prev;
    });
  }

  // URL 변경 시 해당 메뉴 자동 열기
  useEffect(() => {
    const path = location.pathname;

    const newOpenMenus = {
      approvalBox: false,
      archiveBox: false,
      vacationManagement: false,
      settings: false,
    };

    if (
      path.startsWith('/approval/drafter') ||
      path.startsWith('/approval/approver') ||
      path.startsWith('/approval/reference') ||
      path.startsWith('/approval/execution')
    ) {
      newOpenMenus.approvalBox = true;
    } else if (path.startsWith('/approval/temporary-archive')) {
      newOpenMenus.archiveBox = true;
    } else if (path.startsWith('/approval/vacation')) {
      newOpenMenus.vacationManagement = true;
    } else if (path.startsWith('/approval/settings')) {
      newOpenMenus.settings = true;
    }

    setOpenMenus(newOpenMenus);
  }, [location.pathname]);

  // 메뉴가 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  function toggleMenu(menuKey) {
    setOpenMenus((prev) => {
      const isAlreadyOpen = prev[menuKey];
      // 모든 메뉴를 닫고, 현재 메뉴의 상태만 반전시킴 (PC 사이드바와 동일한 동작)
      return {
        approvalBox: false,
        archiveBox: false,
        vacationManagement: false,
        settings: false,
        [menuKey]: !isAlreadyOpen,
      };
    });
  }

  function handleMenuClick(path) {
    navigate(path);
    onClose();
  }

  function isActiveMenu(path) {
    return location.pathname === path;
  }

  // 로그아웃 핸들러
  async function handleLogout() {
    try {
      await postLogout();
    } catch (error) {
      // 서버 로그아웃 실패(네트워크/토큰 만료 등)여도
      // UX 관점에서는 클라이언트 인증정보를 초기화하고 로그인 화면으로 이동합니다.
    } finally {
      // 리액트 쿼리 캐시 전체 삭제
      // clearAuth()와 함께 실행하여 모든 사용자 데이터를 초기화합니다.
      queryClient.clear();
      clearAuth();

      // 모바일 앱(Flutter WebView)인 경우 Flutter로 로그아웃 이벤트 전달
      // WebView의 쿠키, 캐시, 스토리지를 네이티브 레벨에서 삭제
      if (isMobileApp() && window.FlutterChannel?.postMessage) {
        try {
          window.FlutterChannel.postMessage('logout');
        } catch (e) {
          // Flutter 채널 통신 실패 시 무시 (로그아웃 흐름은 계속 진행)
        }
      }

      navigate('/login');
      onClose();
    }
  }

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.menu} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <header className={styles.header}>
          {/* 헤더 상단 - 사용자 정보 영역 */}
          <div className={styles.headerTop}>
            <div className={styles.headerUserInfo}>
              <div className={styles.userProfile}>
                {user?.name && (
                  <div className={styles.userNameContainer}>
                    {user?.company && <span className={styles.companyName}>{user.company}</span>}
                    <div className={styles.userNameRow}>
                      <span className={styles.userName}>
                        <span className={styles.userNameBold}>{user.name}</span>님
                      </span>
                      {/* 계정전환 버튼 - 2개 이상 회사가 있을 때만 표시 */}
                      {showAccountSwitch && (
                        <button
                          type="button"
                          className={styles.accountSwitchButton}
                          onClick={toggleAccountSwitch}
                          disabled={switchCompanyMutation.isPending}
                        >
                          계정전환
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="메뉴 닫기"
            >
              <img src={closeIcon} alt="닫기" className={styles.closeIcon} />
            </button>
          </div>
          {/* 계정전환 드롭다운 - 헤더 내부에서 확장 */}
          {showAccountSwitch && isAccountSwitchOpen && (
            <div className={styles.headerDropdown}>
              {companyList.map((company) => {
                const isCurrentCompany = company.companyCode === companyCode;
                return (
                  <button
                    key={company.companyCode}
                    type="button"
                    className={`${styles.dropdownCompanyItem} ${isCurrentCompany ? styles.dropdownCompanyItemCurrent : ''}`}
                    onClick={() => !isCurrentCompany && handleSwitchCompany(company.companyCode)}
                    disabled={switchCompanyMutation.isPending || isCurrentCompany}
                  >
                    <span className={styles.dropdownCompanyName}>{company.company}</span>
                    {isCurrentCompany && <span className={styles.dropdownCheckIcon}>✓</span>}
                    {switchCompanyMutation.isPending &&
                      switchCompanyMutation.variables === company.companyCode && (
                        <span className={styles.loadingSpinnerSmall} />
                      )}
                  </button>
                );
              })}
            </div>
          )}
        </header>

        {/* 로딩 오버레이 - 계정전환 중 */}
        {switchCompanyMutation.isPending && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingContent}>
              <div className={styles.loadingSpinner} />
              <span className={styles.loadingText}>계정 전환 중...</span>
            </div>
          </div>
        )}

        {/* 메뉴 콘텐츠 */}
        <nav className={styles.nav}>
          {/* 결재함 */}
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => toggleMenu('approvalBox')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={InboxIcon} alt="결재함" />
                </span>
                <span className={styles.menuText}>
                  결재함
                  <ApprovalNotificationDot />
                </span>
              </span>
              <img
                src={ChevronDownIcon}
                alt=""
                className={`${styles.chevron} ${openMenus.approvalBox ? styles.chevronOpen : ''}`}
              />
            </button>
            {openMenus.approvalBox && (
              <div className={styles.subMenu}>
                <button
                  type="button"
                  className={`${styles.subMenuItem} ${isActiveMenu('/approval/approver') ? styles.subMenuItemActive : ''}`}
                  onClick={() => handleMenuClick('/approval/approver')}
                >
                  <span className={styles.subMenuInner}>
                    <span className={styles.indicator} />
                    <span className={styles.subMenuText}>
                      결재문서
                      <ApprovalNotificationDot />
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`${styles.subMenuItem} ${isActiveMenu('/approval/drafter') ? styles.subMenuItemActive : ''}`}
                  onClick={() => handleMenuClick('/approval/drafter')}
                >
                  <span className={styles.subMenuInner}>
                    <span className={styles.indicator} />
                    <span className={styles.subMenuText}>기안문서</span>
                  </span>
                </button>

                <button
                  type="button"
                  className={`${styles.subMenuItem} ${isActiveMenu('/approval/reference') ? styles.subMenuItemActive : ''}`}
                  onClick={() => handleMenuClick('/approval/reference')}
                >
                  <span className={styles.subMenuInner}>
                    <span className={styles.indicator} />
                    <span className={styles.subMenuText}>참조문서</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`${styles.subMenuItem} ${isActiveMenu('/approval/execution') ? styles.subMenuItemActive : ''}`}
                  onClick={() => handleMenuClick('/approval/execution')}
                >
                  <span className={styles.subMenuInner}>
                    <span className={styles.indicator} />
                    <span className={styles.subMenuText}>시행문서</span>
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* 보관함
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => toggleMenu('archiveBox')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={BoxIcon} alt="보관함" />
                </span>
                <span className={styles.menuText}>보관함</span>
              </span>
              <img
                src={ChevronDownIcon}
                alt=""
                className={`${styles.chevron} ${openMenus.archiveBox ? styles.chevronOpen : ''}`}
              />
            </button>
            {openMenus.archiveBox && (
              <div className={styles.subMenu}>
                <button
                  type="button"
                  className={`${styles.subMenuItem} ${isActiveMenu('/approval/temporary-archive') ? styles.active : ''}`}
                  onClick={() => handleMenuClick('/approval/temporary-archive')}
                >
                  임시보관함
                </button>
              </div>
            )}
          </div>
          */}

          {/* 휴가관리 */}
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => toggleMenu('vacationManagement')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={UmbrellaIcon} alt="휴가관리" />
                </span>
                <span className={styles.menuText}>휴가관리</span>
              </span>
              <img
                src={ChevronDownIcon}
                alt=""
                className={`${styles.chevron} ${openMenus.vacationManagement ? styles.chevronOpen : ''}`}
              />
            </button>
            {openMenus.vacationManagement && (
              <div className={styles.subMenu}>
                <button
                  type="button"
                  className={`${styles.subMenuItem} ${isActiveMenu('/approval/vacation/request') ? styles.subMenuItemActive : ''}`}
                  onClick={() => handleMenuClick('/approval/vacation/request')}
                >
                  <span className={styles.subMenuInner}>
                    <span className={styles.indicator} />
                    <span className={styles.subMenuText}>휴가신청</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`${styles.subMenuItem} ${isActiveMenu('/approval/vacation/approval') ? styles.subMenuItemActive : ''}`}
                  onClick={() => handleMenuClick('/approval/vacation/approval')}
                >
                  <span className={styles.subMenuInner}>
                    <span className={styles.indicator} />
                    <span className={styles.subMenuText}>휴가결재</span>
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* 설정 */}
          <div className={styles.menuGroup}>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => toggleMenu('settings')}
            >
              <span className={styles.menuLeft}>
                <span className={styles.menuIcon}>
                  <img src={SettingsIcon} alt="설정" />
                </span>
                <span className={styles.menuText}>설정</span>
              </span>
              <img
                src={ChevronDownIcon}
                alt=""
                className={`${styles.chevron} ${openMenus.settings ? styles.chevronOpen : ''}`}
              />
            </button>
            {openMenus.settings && (
              <div className={styles.subMenu}>
                <button
                  type="button"
                  className={`${styles.subMenuItem} ${isActiveMenu('/approval/settings/sign') ? styles.subMenuItemActive : ''}`}
                  onClick={() => handleMenuClick('/approval/settings/sign')}
                >
                  <span className={styles.subMenuInner}>
                    <span className={styles.indicator} />
                    <span className={styles.subMenuText}>서명설정</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`${styles.subMenuItem} ${isActiveMenu('/approval/settings/password') ? styles.subMenuItemActive : ''}`}
                  onClick={() => handleMenuClick('/approval/settings/password')}
                >
                  <span className={styles.subMenuInner}>
                    <span className={styles.indicator} />
                    <span className={styles.subMenuText}>비밀번호 설정</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`${styles.subMenuItem} ${isActiveMenu('/approval/settings/approval-line') ? styles.subMenuItemActive : ''}`}
                  onClick={() => handleMenuClick('/approval/settings/approval-line')}
                >
                  <span className={styles.subMenuInner}>
                    <span className={styles.indicator} />
                    <span className={styles.subMenuText}>자주 쓰는 결재 라인</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`${styles.subMenuItem} ${isActiveMenu('/approval/settings/notification') ? styles.subMenuItemActive : ''}`}
                  onClick={() => handleMenuClick('/approval/settings/notification')}
                >
                  <span className={styles.subMenuInner}>
                    <span className={styles.indicator} />
                    <span className={styles.subMenuText}>알림설정</span>
                  </span>
                </button>
              </div>
            )}
          </div>
        </nav>

        {/* 푸터 */}
        <footer className={styles.footer}>
          <button type="button" className={styles.versionInfoButton}>
            버전 정보
          </button>
          <button type="button" className={styles.footerLogoutButton} onClick={handleLogout}>
            <img src={logoutIcon} alt="로그아웃" className={styles.footerLogoutIcon} />
            <span>로그아웃</span>
          </button>
        </footer>
      </div>
    </div>
  );
}

MobileMenu.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MobileMenu;
